import { useEffect, useState } from "react";
import { ClipboardList, Handshake, Package, FolderKanban, FileText, Settings as SettingsIcon, LogOut, RefreshCw } from "lucide-react";
import Logo from "@/components/Logo";
import api, { authHeaders } from "@/lib/api";
import AdminLogin from "./AdminLogin";
import LeadsTab from "./LeadsTab";
import AppsTab from "./AppsTab";
import ProjectsTab from "./ProjectsTab";
import ContentTab from "./ContentTab";
import SettingsTab from "./SettingsTab";

const TABS = [
  { id: "leads", label: "LEAD-URI", icon: ClipboardList },
  { id: "partners", label: "PARTENERI", icon: Handshake },
  { id: "suppliers", label: "FURNIZORI", icon: Package },
  { id: "projects", label: "PROIECTE", icon: FolderKanban },
  { id: "content", label: "CONȚINUT & FAQ", icon: FileText },
  { id: "settings", label: "SETĂRI", icon: SettingsIcon },
];

export default function AdminDashboard() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("leads");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    api.get("/admin/me", { headers: authHeaders() })
      .then(({ data }) => setUser(data))
      .catch(() => setUser(null));
  }, []);

  const logout = async () => {
    try {
      await api.post("/admin/logout", {}, { headers: authHeaders() });
    } catch {}
    localStorage.removeItem("brox_admin_token");
    setUser(null);
  };

  if (user === undefined) {
    return <div className="min-h-screen bg-ink-950" data-testid="admin-loading" />;
  }
  if (user === null) {
    return <AdminLogin onLogin={setUser} />;
  }

  return (
    <div className="min-h-screen bg-ink-950" data-testid="admin-dashboard">
      <header className="sticky top-0 z-30 border-b border-white/10 bg-ink-950/90 backdrop-blur-xl">
        <div className="container-brox flex h-16 min-w-0 items-center justify-between gap-2 sm:gap-4">
          <div className="flex shrink-0 items-center gap-4">
            <Logo />
            <span className="hidden border border-copper/40 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-copper sm:block">Admin</span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              onClick={() => setRefreshKey((k) => k + 1)}
              className="inline-flex h-10 items-center gap-2 border border-white/15 px-2.5 font-mono text-[11px] uppercase tracking-wider text-paper/70 transition-colors hover:border-copper hover:text-copper sm:px-3"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw className="h-4 w-4" />
              <span className="hidden sm:inline">Reîmprospătează</span>
              <span className="sr-only">Reîmprospătează</span>
            </button>
            <button
              onClick={logout}
              className="inline-flex h-10 items-center gap-2 border border-white/15 px-3 font-mono text-[11px] uppercase tracking-wider text-paper/70 transition-colors hover:border-red-500 hover:text-red-400"
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-4 w-4" />
              Ieși
            </button>
          </div>
        </div>
        <nav className="scroll-fade-x container-brox flex items-center gap-1 overflow-x-auto pb-2" aria-label="Secțiuni admin" data-testid="admin-tabs">
            <span className="mr-1 hidden shrink-0 items-center font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground min-[420px]:flex sm:hidden">Glisează →</span>
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex min-h-[40px] shrink-0 items-center gap-2 px-4 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${
                tab === t.id ? "bg-copper text-ink-950" : "text-paper/60 hover:bg-white/5 hover:text-copper"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              <t.icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="container-brox py-8">
        {tab === "leads" && <LeadsTab key={`leads-${refreshKey}`} />}
        {tab === "partners" && <AppsTab key={`partners-${refreshKey}`} endpoint="/admin/partners" kind="partner" />}
        {tab === "suppliers" && <AppsTab key={`suppliers-${refreshKey}`} endpoint="/admin/suppliers" kind="supplier" />}
        {tab === "projects" && <ProjectsTab key={`projects-${refreshKey}`} />}
        {tab === "content" && <ContentTab key={`content-${refreshKey}`} />}
        {tab === "settings" && <SettingsTab key={`settings-${refreshKey}`} />}
      </main>
    </div>
  );
}
